import {
  PresetReorderDetails,
  ReorderDetails
} from "@saleor/coupons/components/CouponRankingList";
import CouponRankingListPage from "@saleor/coupons/components/CouponRankingListPage";
import {
  useCouponManualReorder,
  useCouponPresetReorder
} from "@saleor/coupons/mutations";
import { useCouponListQuery } from "@saleor/coupons/queries";
import {
  couponListUrl,
  couponRankingListUrl,
  CouponsListUrlQueryParams
} from "@saleor/coupons/url";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import {
  CouponDealTypeEnum,
  CouponSortField,
  CouponStatusEnum,
  OrderDirection
} from "@saleor/types/globalTypes";
import React from "react";

import { areFiltersApplied, getFilterTabs } from "../CouponList/filters";

interface CouponRankingListProps {
  params: CouponsListUrlQueryParams;
}

export const CouponRankingList: React.FC<CouponRankingListProps> = ({
  params
}) => {
  const shop = useShop();
  const currencySymbol = shop?.defaultCurrency || "PHP";
  const navigate = useNavigator();
  const notify = useNotifier();
  const queryVariables = React.useMemo(() => ({}), [params]);

  const couponTabs = [
    {
      data: {
        dealType: CouponDealTypeEnum.DEALS
      },
      name: "Mall Deals"
    },
    {
      data: {
        dealType: CouponDealTypeEnum.EVENTS
      },
      name: "Mall Events"
    },
    ...getFilterTabs()
  ];

  const currentTabIndex = parseInt(params.activeTab || "0", 10);
  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? couponTabs.length
        : 0
      : parseInt(params.activeTab, 0);

  const isDealsTab =
    couponTabs[currentTabIndex]?.data.dealType === CouponDealTypeEnum.DEALS;
  const dealType = isDealsTab
    ? CouponDealTypeEnum.DEALS
    : CouponDealTypeEnum.EVENTS;

  const activeStatuses = [
    CouponStatusEnum.ACTIVE,
    CouponStatusEnum.INACTIVE,
    CouponStatusEnum.SCHEDULED,
    CouponStatusEnum.COMPLETED
  ];

  const { data, loading, refetch } = useCouponListQuery({
    displayLoader: true,
    variables: {
      filter: {
        dealType: [dealType],
        isFeatured: true,
        status: activeStatuses
      },
      first: 100,
      ...queryVariables,
      sortBy: {
        direction: OrderDirection.DESC,
        field: CouponSortField.ORDERED_FEATURED_NAME
      }
    }
  });

  const handleTabChange = (tab: string | number) => {
    navigate(
      couponRankingListUrl({
        ...params,
        activeTab: tab.toString(),
        ...couponTabs[tab].data
      })
    );
  };

  const [couponManualReorder] = useCouponManualReorder({
    onCompleted: data => {
      if (data.couponManualReorder.errors.length === 0) {
        notify({
          status: "success",
          text: "Coupons reordered successfully."
        });
        refetch();
      } else {
        notify({
          status: "error",
          text: data.couponManualReorder.errors[0].message
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message
      });
    }
  });

  const handleReorder = (reorderDetails: ReorderDetails) => {
    const { movedCouponId, beforeItemId, afterItemId } = reorderDetails;

    const couponOrderGroups = [
      {
        coupons: [movedCouponId],
        insertAfter: beforeItemId || null,
        insertBefore: afterItemId || null
      }
    ];
    couponManualReorder({
      variables: { couponOrderGroups }
    });
  };

  const [presetReorderCoupons] = useCouponPresetReorder({
    onCompleted: data => {
      if (data.couponPresetReorder.errors.length === 0) {
        notify({
          status: "success",
          text: "Coupon reordered successfully?"
        });
        refetch();
      } else {
        notify({
          status: "error",
          text: data.couponPresetReorder.errors[0].message
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message
      });
    }
  });

  const handlePresetReordering = async (
    presetReorderDetails: PresetReorderDetails
  ) => {
    const { couponId, reorderPreset } = presetReorderDetails;
    try {
      await presetReorderCoupons({
        variables: {
          input: {
            couponId,
            reorderPreset
          }
        }
      });
    } catch (error) {
      notify({
        status: "error",
        text: error.message
      });
    }
  };

  return (
    <>
      <CouponRankingListPage
        currencySymbol={currencySymbol}
        brands={undefined}
        onAdd={undefined}
        disabled={loading}
        onNextPage={undefined}
        onPreviousPage={undefined}
        onRowClick={undefined}
        currentTab={currentTab}
        tabs={couponTabs.map(tab => tab.name)}
        onTabChange={handleTabChange}
        onTabDelete={undefined}
        onTabSave={undefined}
        onBack={() => navigate(couponListUrl(params))}
        couponManageUrl={couponListUrl(params)}
        onReorder={handleReorder}
        coupons={maybe(() => data?.coupons.edges.map(edge => edge.node), [])}
        filterOpts={undefined}
        onFilterChange={undefined}
        initialSearch={""}
        onSearchChange={undefined}
        onPresetReorder={handlePresetReordering}
        refetch={refetch}
      />
    </>
  );
};

export default CouponRankingList;
